// Memory Web nodes (docs/05 §3): the root, the gates, The Hand, and the six stat branches.
// Edges live in webLayout.ts.
import { defineNode, type NodeDef } from './defs';
import { addTo } from '../sim/util';
import { B } from './balance';
import type { Modifiers } from '../sim/modifiers';
import type { SizeId } from '../sim/types';

const SIZE_RANK: SizeId[] = ['bandit', 'cabinet', 'wide', 'grand', 'tall', 'cathedral'];
/** Grow the machine to at least `size` (gates may be owned in any order in the save). */
export const growTo = (m: Modifiers, size: SizeId): void => {
  if (SIZE_RANK.indexOf(size) > SIZE_RANK.indexOf(m.size)) m.size = size;
};
const gate = (id: string, baseCost: number, mods: NodeDef['mods'], extra: Partial<NodeDef> = {}): NodeDef =>
  defineNode({ id, kind: 'gate', branch: 'gate', maxLevel: 1, baseCost, mods, ...extra });

const gates: NodeDef[] = [
  defineNode({ id: 'ROOT', kind: 'root', branch: 'root', maxLevel: 1, baseCost: 0 }),
  defineNode({
    id: 'G_PAIR',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 2,
    mods: (m) => void m.patterns.add('pair'),
  }),
  defineNode({
    id: 'G_HOLD',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 3,
    mods: (m) => {
      m.features.add('hold');
      m.holdPerWindow += 1;
    },
  }),
  defineNode({
    id: 'G_DRAFT',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 5,
    mods: (m) => void m.features.add('draft'),
  }),
  defineNode({
    id: 'G_NUDGE',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 8,
    mods: (m) => {
      m.features.add('nudge');
      m.nudgePerWindow += 1;
    },
  }),
  defineNode({
    id: 'G_CHARMS',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 15,
    mods: (m) => {
      m.features.add('shop');
      m.charmSlots += 2;
    },
  }),
  defineNode({
    id: 'G_BONUS_SCRATCH',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 20,
    mods: (m) => {
      m.bonuses.add('scratch');
      m.symbolGates.add('scratch');
    },
  }),
  defineNode({
    id: 'G_GROW_1',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 60,
    requiresAll: ['G_NUDGE', 'G_CHARMS'],
    mods: (m) => {
      growTo(m, 'cabinet');
      m.features.add('face');
      m.symbolGates.add('awake');
      m.reckonings = true;
    },
  }),
  defineNode({
    id: 'G_FEED',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 40,
    mods: (m) => void m.features.add('feed'),
  }),
  defineNode({
    id: 'G_ROT',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 100,
    mods: (m) => void m.symbolGates.add('rot'),
  }),
  defineNode({
    id: 'G_COLUMNS',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 40,
    mods: (m) => void m.patterns.add('col3'),
  }),
  defineNode({
    id: 'G_DIAGONALS',
    kind: 'gate',
    branch: 'gate',
    maxLevel: 1,
    baseCost: 60,
    mods: (m) => void m.patterns.add('diag3'),
  }),
  // Act II
  gate('G_PROPOSITIONS', 90, (m) => void m.features.add('deals')),
  gate('G_BONUS_HILO', 120, (m) => {
    m.bonuses.add('hilo');
    m.symbolGates.add('hilo');
  }),
  gate('G_GROW_2', 150, (m) => {
    growTo(m, 'wide');
    m.patterns.add('line4');
  }),
  gate('G_HEX', 260, (m) => void m.symbolGates.add('hex')),
  gate('G_BONUS_WHEEL', 400, (m) => {
    m.bonuses.add('wheel');
    m.symbolGates.add('wheel');
  }),
  gate('G_GROW_3', 420, (m) => {
    growTo(m, 'grand');
    m.patterns.add('line5');
  }),
  gate('G_VEE', 800, (m) => void m.patterns.add('vee')),
  gate('G_ZIG', 800, (m) => void m.patterns.add('zig')),
  gate('G_MOLT', 900, (m) => void m.features.add('molt'), { requiresMaxTribute: B.MOLT_MIN_TRIBUTE }),
  // Act III–IV
  gate('G_EYES', 500, (m) => {
    m.features.add('eyes');
    m.symbolGates.add('eyes');
  }),
  gate('G_EYE_PATTERN', 700, (m) => void m.patterns.add('eye')),
  gate('G_CROSS', 900, (m) => void m.patterns.add('cross')),
  gate('G_BONUS_PLINKO', 1000, (m) => {
    m.bonuses.add('plinko');
    m.symbolGates.add('plinko');
  }),
  gate('G_GROW_4', 1500, (m) => growTo(m, 'tall')),
  gate('G_BONUS_FEAST', 3000, (m) => {
    m.bonuses.add('feast');
    m.symbolGates.add('feast');
  }),
  gate('G_GROW_5', 5000, (m) => {
    growTo(m, 'cathedral');
    m.lastSupper = true;
  }),
];

// The Hand (docs/04 §21, docs/05 §3.1): permanent automation, branching off H_AUTOSPIN.
const handNode = (id: string, maxLevel: number, baseCost: number, key: string): NodeDef =>
  defineNode({ id, kind: 'hand', branch: 'hand', maxLevel, baseCost, mods: (m, l) => void (m.hand[key] = l) });

const hand: NodeDef[] = [
  handNode('H_AUTOSPIN', 1, 320, 'autospin'),
  handNode('H_SPEED', 5, 800, 'speed'),
  handNode('H_AUTOPAY', 1, 1000, 'autopay'),
  handNode('H_AUTOHOLD', 1, 1500, 'autohold'),
  handNode('H_AUTOFEED', 1, 1500, 'autofeed'),
  handNode('H_AUTONUDGE', 1, 2000, 'autonudge'),
  handNode('H_AUTODRAFT', 1, 3000, 'autodraft'),
  handNode('H_AUTOBONUS', 1, 4000, 'autobonus'),
];

const stat = (
  id: string,
  branch: NodeDef['branch'],
  maxLevel: number,
  baseCost: number,
  mods: NodeDef['mods'],
  requiresAll?: string[],
): NodeDef => defineNode({ id, kind: 'stat', branch, maxLevel, baseCost, mods, requiresAll });

const fortune: NodeDef[] = [
  stat('FO01', 'fortune', 3, 3, (m, l) => void (m.luck += l)),
  stat('FO02', 'fortune', 1, 8, (m) => void (m.pityAfter = Math.min(m.pityAfter, 2))),
  stat('FO03', 'fortune', 1, 12, (m) => void (m.hotHandLuck += 1)),
  stat('FO04', 'fortune', 1, 20, (m) => void (m.perfectPullLuck = Math.max(m.perfectPullLuck, 2))),
  stat('FO05', 'fortune', 2, 30, (m, l) => addTo(m.luckById, 'clover', l)),
  stat('FO06', 'fortune', 1, 40, (m) => void (m.secondChance = true)),
  stat('FO07_RARITY', 'fortune', 3, 15, (m, l) => void (m.rarityBoost += 0.25 * l)),
  stat('FO08', 'fortune', 1, 120, (m) => void (m.draftChoices = Math.max(m.draftChoices, 4))),
  stat('FO09', 'fortune', 1, 60, (m) => void (m.peekRows += 1)),
  stat('FO10', 'fortune', 1, 200, (m) => void (m.loadedFate = true)),
  stat('FO11', 'fortune', 1, 90, (m) => void (m.omenWindows = Math.max(m.omenWindows, 2))),
  stat('FO12', 'fortune', 1, 500, (m) => void (m.favoredLuckPer5 += 1)),
  stat('FO13', 'fortune', 1, 2000, (m) => void (m.serendipity = Math.max(m.serendipity, B.SERENDIPITY))),
  stat('FO14', 'fortune', 1, 10000, (m) => void (m.fateCap = Math.max(m.fateCap, B.DESTINY_FATE_CAP))),
];

const greed: NodeDef[] = [
  stat('GR01', 'greed', 5, 2, (m, l) => void (m.valueAll += l)),
  stat('GR02', 'greed', 3, 5, (m, l) => addTo(m.pctByTag, 'fruit', 0.5 * l)),
  stat('GR03', 'greed', 3, 5, (m, l) => addTo(m.pctByTag, 'classic', 0.5 * l)),
  stat('GR04', 'greed', 3, 10, (m, l) => addTo(m.valueById, 'seven', 3 * l)),
  stat('GR05', 'greed', 3, 4, (m, l) => {
    addTo(m.valueById, 'coin', 2 * l);
    addTo(m.valueById, 'moneybag', 2 * l);
  }),
  stat('GR06', 'greed', 3, 15, (m, l) => addTo(m.patternMultAdd, 'line3', 0.5 * l)),
  stat('GR07', 'greed', 2, 10, (m, l) => addTo(m.patternMultAdd, 'pair', 0.25 * l), ['G_PAIR']),
  stat('GR08', 'greed', 5, 50, (m, l) => void m.xMult.push({ source: 'GR08', x: Math.pow(1.1, l) })),
  stat('GR09', 'greed', 3, 80, (m, l) => void (m.compoundPct += 0.05 * l)),
  stat('GR10', 'greed', 1, 150, (m) => void (m.jackpotMultX *= 2)),
  stat('GR11', 'greed', 2, 400, (m, l) => addTo(m.pctByRarity, 'L', 0.5 * l)),
  stat('GR12', 'greed', 1, 300, (m) => void (m.verminEconomy += 1), ['G_GROW_1']),
  stat('GR13', 'greed', 4, 250, (m, l) => void (m.patternMultAll += 0.25 * l)),
  stat('GR14', 'greed', 1, 3000, (m) => void (m.midas += B.MIDAS)),
];

const hands: NodeDef[] = [
  stat('HA01', 'hands', 3, 10, (m, l) => void (m.holdPerWindow += l), ['G_HOLD']),
  stat('HA02', 'hands', 3, 10, (m, l) => void (m.nudgePerWindow += l), ['G_NUDGE']),
  stat('HA03_MUSCLE_MEMORY', 'hands', 1, 25, (m) => void (m.perfectPullAnyInput = true)),
  stat('HA04', 'hands', 1, 60, (m) => void (m.perfectPullXMult *= 1.25)),
  stat('HA05_NUDGE_SIGHT', 'hands', 1, 20, (m) => void (m.nudgeSight = true), ['G_NUDGE']),
  stat('HA06', 'hands', 1, 150, (m) => void (m.longNudge = true), ['G_NUDGE']),
  stat('HA07', 'hands', 1, 100, (m) => void (m.holdCarryMax = Math.max(m.holdCarryMax, 3)), ['G_HOLD']),
  stat('HA08', 'hands', 2, 70, (m, l) => void (m.freeNudgeChance += 0.1 * l), ['G_NUDGE']),
  stat('HA09', 'hands', 1, 200, (m) => void (m.heldValue += 1), ['G_HOLD']),
  stat('HA10', 'hands', 1, 45, (m) => void (m.deadSpinNudgeRefund += 1), ['G_NUDGE']),
];

const contracts: NodeDef[] = [
  stat('CO01', 'contracts', 5, 4, (m, l) => void (m.tributeMult *= 1 - 0.03 * l)),
  stat('CO02', 'contracts', 5, 8, (m, l) => void (m.soulMult *= Math.pow(1.2, l))),
  stat('CO03', 'contracts', 3, 12, (m, l) => void (m.payEarlyPct += 0.1 * l)),
  stat('CO04', 'contracts', 2, 10, (m, l) => void (m.startCoinsT1 = Math.max(m.startCoinsT1, l >= 2 ? 5 : 2))),
  stat('CO05', 'contracts', 2, 30, (m, l) => void (m.spinsPerWindow += l)),
  stat('CO06', 'contracts', 3, 40, (m, l) => void (m.shopDiscount += 0.1 * l), ['G_CHARMS']),
  stat('CO07_ASCETIC', 'contracts', 1, 20, (m) => void (m.skipDraftSouls += 1), ['G_DRAFT']),
  stat('CO08', 'contracts', 2, 250, (m, l) => void (m.startTribute = Math.max(m.startTribute, l >= 2 ? 5 : 3))),
  stat('CO09', 'contracts', 1, 300, (m) => void (m.reckoningTributeMult = Math.min(m.reckoningTributeMult, B.DEBT_RELIEF_MULT)), ['G_GROW_1']),
  stat('CO10', 'contracts', 2, 150, (m, l) => void (m.soulsPerReckoning += 2 * l), ['G_GROW_1']),
  stat('CO11', 'contracts', 1, 600, (m) => void (m.dealChoices = Math.max(m.dealChoices, 2)), ['G_PROPOSITIONS']),
  stat('CO12', 'contracts', 1, 80, (m) => void (m.startCommonCharm = true), ['G_CHARMS']),
  stat('CO13', 'contracts', 1, 500, (m) => void (m.shopCharms = Math.max(m.shopCharms, 4)), ['G_CHARMS']),
  stat('CO14', 'contracts', 1, 50, (m) => void (m.charmSlots += 1), ['G_CHARMS']),
  stat('CO15', 'contracts', 1, 500, (m) => void (m.charmSlots += 1), ['G_CHARMS']),
  stat('CO16', 'contracts', 1, 5000, (m) => void (m.charmSlots += 1), ['G_CHARMS']),
];

const appetite: NodeDef[] = [
  stat('AP01', 'appetite', 3, 6, (m, l) => void (m.hungerGainPct -= 0.1 * l), ['G_GROW_1']),
  stat('AP02', 'appetite', 1, 15, (m) => void (m.feedPct += 0.5), ['G_FEED']),
  stat('AP03', 'appetite', 1, 25, (m) => void (m.feedTierPar += 1), ['G_FEED']),
  stat('AP04', 'appetite', 2, 60, (m, l) => void (m.ravenousX += 0.25 * l), ['G_GROW_1']),
  stat('AP05', 'appetite', 1, 120, (m) => void (m.pickyEater = true), ['G_GROW_1']),
  stat('AP06', 'appetite', 2, 30, (m, l) => void (m.rotAgeAdd += 2 * l), ['G_ROT']),
  stat('AP07', 'appetite', 1, 80, (m) => void (m.compostChance = Math.max(m.compostChance, B.COMPOST_CHANCE)), ['G_ROT']),
  stat('AP08_LAST_MEAL', 'appetite', 1, 300, (m) => void (m.lastMeal = true), ['G_CHARMS']),
  stat('AP09', 'appetite', 1, 500, (m) => void (m.doomDecayX = 2), ['G_EYES']),
  stat('AP10', 'appetite', 2, 700, (m, l) => void (m.devourChanceX *= 1 - 0.25 * l), ['G_EYES']),
  stat('AP11', 'appetite', 1, 90, (m) => void (m.contentLuck += 1), ['G_GROW_1']),
  stat('AP12', 'appetite', 1, 200, (m) => void (m.adoringWindows = Math.min(m.adoringWindows, 2)), ['G_GROW_1']),
  stat('AP13', 'appetite', 1, 1500, (m) => void (m.eatXMult += 0.1), ['G_GROW_1']),
];

const rituals: NodeDef[] = [
  stat('RI01', 'rituals', 2, 15, (m, l) => void (m.scratchBrush += 0.25 * l), ['G_BONUS_SCRATCH']),
  stat('RI02', 'rituals', 2, 30, (m, l) => void (m.scratchNoTrap += 0.2 * l), ['G_BONUS_SCRATCH']),
  stat('RI03', 'rituals', 2, 25, (m, l) => void (m.sigilDraftX += 0.5 * l)),
  stat('RI04', 'rituals', 1, 150, (m) => void (m.scratchSize = Math.max(m.scratchSize, 4)), ['G_BONUS_SCRATCH']),
  stat('RI05', 'rituals', 1, 60, (m) => void (m.hiloHistory = Math.max(m.hiloHistory, 5)), ['G_BONUS_HILO']),
  stat('RI06', 'rituals', 1, 100, (m) => void (m.hiloSafeRung = true), ['G_BONUS_HILO']),
  stat('RI07', 'rituals', 2, 120, (m, l) => void (m.wheelBand += 0.5 * l), ['G_BONUS_WHEEL']),
  stat('RI08', 'rituals', 2, 200, (m, l) => void (m.nightShiftBonus += 2 * l), ['G_BONUS_WHEEL']),
  stat('RI09', 'rituals', 2, 400, (m, l) => void (m.plinkoBalls += l), ['G_BONUS_PLINKO']),
  stat('RI10', 'rituals', 1, 700, (m) => void (m.goldenPegs = Math.max(m.goldenPegs, 2)), ['G_BONUS_PLINKO']),
  stat('RI11', 'rituals', 1, 3000, (m) => void (m.feastRespins = Math.max(m.feastRespins, 4)), ['G_BONUS_FEAST']),
  stat('RI12', 'rituals', 3, 250, (m, l) => void (m.bonusPayoutX *= Math.pow(1.25, l))),
  stat('RI13', 'rituals', 1, 1200, (m) => void (m.grinIsSigil = true)),
];

export const NODE_LIST: NodeDef[] = [...gates, ...hand, ...fortune, ...greed, ...hands, ...contracts, ...appetite, ...rituals];
